<template>
  <div class="mortgage-documents-page">
    <loader v-if="loading" />
    <div v-else class="padding-l">
      <div class="header-section">
        <h1 class="ri-small-display">מסמכי משכנתא</h1>
      </div>

      <section v-if="requiredDocuments.length > 0" class="documents-section margin-top-l">
        <div class="section-title ri-large-body margin-bottom-sm">
          <span class="ri-font-weight-bold">מסמכים שצריך להעלות</span>
          <span class="section-number">{{ documentsToUploadSectionNumber }}</span>
        </div>
        <document-card
          v-for="document in requiredDocuments"
          :key="document.documentType"
          :document="document"
          @upload="handleUpload"
        />
      </section>

      <section v-if="uploadedDocuments.length > 0" class="documents-section margin-top-xl">
        <div class="section-title ri-large-body margin-bottom-sm">
          <span class="ri-font-weight-bold">מסמכים קיימים</span>
          <span class="section-number">{{ uploadedDocumentsSectionNumber }}</span>
        </div>
        <document-card
          v-for="document in uploadedDocuments"
          :key="document.documentType"
          :document="document"
          @upload="handleUpload"
          @download="handleDownload"
        />
      </section>
    </div>

    <!-- File upload input -->
    <input
      ref="fileInput"
      type="file"
      accept=".pdf,.jpg,.jpeg,.png"
      style="display: none;"
      @change="onFileSelected"
    />
  </div>
</template>

<script>
import BaseUI from '@riseupil/base-ui';
import { mapActions, mapGetters, mapState } from 'vuex';
import Segment from '@/Segment';
import mortgagesApi from '@/api/MortgagesApi';
import DocumentCard from './components/DocumentCard.vue';

export default {
  name: 'MortgageDocumentsCollectionPage',
  components: {
    Loader: BaseUI.TinyLoader,
    DocumentCard,
  },
  data() {
    return {
      selectedDocumentType: null,
      uploadingFile: false,
    };
  },
  async created() {
    Segment.trackUserInteraction('MortgageDocumentsCollectionPage_Entered');
    await this.fetchMortgageDocuments();
    this.setAppHeaderProps({
      backgroundColorVariant: BaseUI.LayoutConsts.COLOR_VARIANT.BEIGE,
      textColor: BaseUI.Colors.riseupBlack,
      logoColor: BaseUI.Colors.riseupBlack,
      bottomBorder: false,
    });
  },
  computed: {
    ...mapState('mortgageDocuments', ['loading', 'documents']),
    ...mapGetters('mortgageDocuments', ['requiredDocuments', 'uploadedDocuments']),
    documentsToUploadSectionNumber() {
      return this.requiredDocuments.length;
    },
    uploadedDocumentsSectionNumber() {
      return this.uploadedDocuments.length;
    },
  },
  methods: {
    ...mapActions('mortgageDocuments', ['fetchMortgageDocuments']),
    ...mapActions('appHeader', ['setAppHeaderProps']),
    handleUpload(document) {
      this.selectedDocumentType = document.documentType;
      this.$refs.fileInput.click();
    },
    async onFileSelected(event) {
      const file = event.target.files[0];

      if (!file) {
        return;
      }

      await this.uploadFile(file);
      this.$refs.fileInput.value = '';
    },
    async uploadFile(file) {
      this.uploadingFile = true;
      try {
        const arrayBuffer = await file.arrayBuffer();
        const buffer = Buffer.from(arrayBuffer);
        await mortgagesApi.uploadMortgageDocumentFile(this.selectedDocumentType, buffer, file.name, file.type);

        Segment.trackUserInteraction('MortgageDocumentsCollectionPage_FileUploaded', {
          documentType: this.selectedDocumentType,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        });

        await this.fetchMortgageDocuments();
      } catch (error) {
        console.error('File upload error:', error);
        Segment.trackUserInteraction('MortgageDocumentsCollectionPage_FileUploadError', {
          error: error.message,
          status: error.response?.status,
          documentType: this.selectedDocumentType,
        });
      } finally {
        this.uploadingFile = false;
        this.selectedDocumentType = null;
      }
    },
    async handleDownload(document) {
      try {
        const blob = await mortgagesApi.downloadMortgageDocumentFile(document.documentType);
        const url = window.URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = document.fileName || `${document.documentType}.pdf`;
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download error:', error);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@riseupil/base-ui/src/scss/riseup-colors";
@import "~@riseupil/base-ui/src/scss/spacings";

.mortgage-documents-page {
  background: #FCF7ED;
  flex: 1;
  .header-section {
    text-align: right;
  }

  .documents-section {
    .section-title {
      display: flex;
      justify-content: space-between;
    }
  }
}
</style>
